import { useEffect, useMemo, useState } from 'react';
import cn from 'classnames';

import {
    Button,
    ButtonKind,
    Footer,
    Status,
    StatusType,
} from '../../shared/ModalV2';
import Selector from '../../shared/Selector';
import { TagRelationPreview } from './TagRelationPreview';
import { Loading } from '../../shared';

import { useAuthenticate, useEnvironments } from '../../hooks/environments';
import { useCreateTag, useTags, useUpdateTagInEnv } from '../../hooks/tags';

import { copyMedias } from '../../utils/upload';
import {
    buildTagCopyEntry,
    hasTagRelationsChanged,
    resolveTargetTagIds,
} from '../utils';

import { Environment } from '../../types/environmentTypes';
import { CreateOrUpdateTag, Tag } from '../../types/tagTypes';

import styles from './CopyTag.module.scss';

type CopyTagProps = {
    tagIds: number[];
    onClose: () => void;
};

type CopiedTag = {
    sourceTagId: number;
    targetTagId: number | null;
    action: 'created' | 'updated' | 'failed';
    stringId: string;
    error?: string;
};

function CopyTag(props: CopyTagProps) {
    const { tagIds, onClose } = props;
    const [targetEnvName, setTargetEnvName] = useState<string>('');
    const [isCopying, setIsCopying] = useState<boolean>(false);
    const [copyError, setCopyError] = useState<string | undefined>();
    const [copiedTags, setCopiedTags] = useState<CopiedTag[]>([]);
    const [activeTabIndex, setActiveTabIndex] = useState<number>(0);

    const {
        environments = [],
        isLoading: isEnvsLoading,
        error: envsError,
    } = useEnvironments();
    const {
        isAuthenticated,
        isLoading: isAuthLoading,
        error: authError,
    } = useAuthenticate(targetEnvName);
    const currentEnv = environments.find((env) => env.active);
    const otherEnvs = environments.filter((env) => !env.active);
    const targetEnv = environments.find((env) => env.name === targetEnvName);
    const targetEnvBasePath = targetEnv?.basepath;

    const {
        tags: sourceTags,
        isLoading: isSourceTagsLoading,
        errorMessage: sourceTagsError,
    } = useTags();
    const {
        tags: targetTags,
        isLoading: isTargetTagsLoading,
        fetch: refetchTargetTags,
        errorMessage: targetTagsError,
    } = useTags({ basepath: targetEnvBasePath });
    const { createTag } = useCreateTag({ basepath: targetEnvBasePath });
    const { updateTag: updateTagInEnv } = useUpdateTagInEnv({
        basepath: targetEnvBasePath,
    });

    const sourceTagsToCopy = useMemo<Tag[]>(() => {
        const byId = new Map(sourceTags.map((t) => [t.id, t]));
        return [...new Set(tagIds)]
            .map((id) => byId.get(id))
            .filter((t): t is Tag => Boolean(t));
    }, [sourceTags, tagIds]);

    const isMulti = tagIds.length > 1;

    useEffect(() => {
        if (targetEnvName && isAuthenticated) {
            refetchTargetTags();
        }
    }, [targetEnvName, isAuthenticated]);

    async function copyIcon(
        source: Tag,
        target: Environment,
    ): Promise<number | null> {
        if (!source.iconMedia) return null;
        let failed = false;
        const [newMedia] = await copyMedias(
            [source.iconMedia],
            source.stringId,
            target,
            (_res, success) => {
                if (!success) failed = true;
            },
        );
        if (failed) {
            throw new Error(`Failed to copy icon for tag ${source.stringId}`);
        }
        return newMedia?.mediaId ?? null;
    }

    async function copyAll(target: Environment) {
        setCopyError(undefined);
        setIsCopying(true);
        const results: CopiedTag[] = [];

        try {
            // Best-effort icon copy for transitively pulled-in (closure) tags:
            // a failed icon copy drops the icon rather than failing the whole
            // copy. Selected tags get their icon copied again below, where
            // failures are surfaced per-tag.
            const copyIconForClosure = async (
                source: Tag,
            ): Promise<number | null> => {
                if (!source.iconMedia) return null;
                try {
                    return await copyIcon(source, target);
                } catch {
                    return null;
                }
            };

            await resolveTargetTagIds(
                sourceTagsToCopy.map((t) => t.id),
                sourceTags,
                targetTags,
                {
                    createTag,
                    updateTag: updateTagInEnv,
                    copyIconFor: copyIconForClosure,
                },
            );

            const { data: refreshed = targetTags } = await refetchTargetTags();
            const targetByStringId = new Map(
                refreshed.map((t) => [t.stringId, t]),
            );
            const originalStringIds = new Set(
                targetTags.map((t) => t.stringId),
            );

            for (const source of sourceTagsToCopy) {
                try {
                    const iconMediaId = await copyIcon(source, target);

                    const targetParentIds = source.parents
                        .map((id) => sourceTags.find((t) => t.id === id))
                        .filter((t): t is Tag => Boolean(t))
                        .map((t) => targetByStringId.get(t.stringId)?.id)
                        .filter((id): id is number => typeof id === 'number');
                    const targetChildIds = source.children
                        .map((id) => sourceTags.find((t) => t.id === id))
                        .filter((t): t is Tag => Boolean(t))
                        .map((t) => targetByStringId.get(t.stringId)?.id)
                        .filter((id): id is number => typeof id === 'number');

                    const body: CreateOrUpdateTag = {
                        stringId: source.stringId,
                        name: source.name,
                        synonyms: source.synonyms,
                        parents: targetParentIds,
                        children: targetChildIds,
                        iconMediaId,
                    };

                    const existedBefore = originalStringIds.has(
                        source.stringId,
                    );
                    const targetTag = targetByStringId.get(source.stringId);
                    if (!targetTag) {
                        throw new Error(
                            `Target tag ${source.stringId} disappeared after creation`,
                        );
                    }
                    await updateTagInEnv(targetTag.id, body);
                    results.push({
                        sourceTagId: source.id,
                        targetTagId: targetTag.id,
                        action: existedBefore ? 'updated' : 'created',
                        stringId: source.stringId,
                    });
                } catch (err) {
                    results.push({
                        sourceTagId: source.id,
                        targetTagId: null,
                        action: 'failed',
                        stringId: source.stringId,
                        error: err instanceof Error ? err.message : String(err),
                    });
                }
            }
        } catch (err) {
            setCopyError(
                err instanceof Error
                    ? err.message
                    : 'Failed to copy tag to target environment',
            );
        } finally {
            setCopiedTags(results);
            setIsCopying(false);
        }
    }

    if (
        isEnvsLoading ||
        isAuthLoading ||
        isSourceTagsLoading ||
        (targetEnvName && isTargetTagsLoading) ||
        isCopying
    ) {
        return (
            <>
                <Loading />
                <Footer>
                    <Button kind={ButtonKind.SECONDARY} onClick={onClose}>
                        Close
                    </Button>
                </Footer>
            </>
        );
    }

    if (
        envsError ??
        (targetEnvName && authError) ??
        sourceTagsError ??
        (targetEnvName && targetTagsError) ??
        (copyError && copiedTags.length === 0)
    ) {
        return (
            <>
                <Status
                    type={StatusType.Error}
                    header="Oops, something went wrong"
                    body={
                        copyError ??
                        'Please try again or contact system administrator.'
                    }
                />
                <Footer>
                    <Button kind={ButtonKind.SECONDARY} onClick={onClose}>
                        Close
                    </Button>
                </Footer>
            </>
        );
    }

    if (copiedTags.length > 0 && targetEnv) {
        const host = targetEnv.basepath.replace('/api', '');
        const failedCount = copiedTags.filter(
            (c) => c.action === 'failed',
        ).length;
        const successCount = copiedTags.length - failedCount;
        const header =
            failedCount === 0
                ? isMulti
                    ? `${successCount} tag(s) successfully copied to ${targetEnvName}`
                    : `Tag successfully ${copiedTags[0].action} in ${targetEnvName}`
                : `${successCount} of ${copiedTags.length} tag(s) copied to ${targetEnvName} — ${failedCount} failed`;
        return (
            <>
                <Status
                    type={
                        failedCount === 0 ? StatusType.Info : StatusType.Error
                    }
                    header={header}
                    body={
                        <div className={styles.successBody}>
                            <div>
                                You can now close the pop up. Please verify the
                                tag{isMulti ? 's' : ''} in the selected
                                environment.
                            </div>
                            <ul className={styles.successList}>
                                {copiedTags.map((c) => (
                                    <li key={c.sourceTagId}>
                                        {c.action === 'failed' ? (
                                            <span>
                                                {c.stringId} (failed
                                                {c.error ? `: ${c.error}` : ''})
                                            </span>
                                        ) : (
                                            <a
                                                href={`${host}/tags/${c.targetTagId}`}
                                                target="_blank"
                                                rel="noreferrer"
                                            >
                                                {c.stringId} ({c.action})
                                            </a>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    }
                />
                <Footer>
                    <Button kind={ButtonKind.SECONDARY} onClick={onClose}>
                        Close
                    </Button>
                </Footer>
            </>
        );
    }

    if (sourceTagsToCopy.length === 0) return null;

    const safeTabIndex = Math.min(activeTabIndex, sourceTagsToCopy.length - 1);
    const activeTag = sourceTagsToCopy[safeTabIndex];
    const activeCopyEntry = targetEnvName
        ? buildTagCopyEntry(activeTag, targetTags)
        : undefined;
    const iconUrl = activeTag.iconMedia?.url.replace('{0}', '@2x');
    const targetIconUrl = activeCopyEntry?.targetTag?.iconMedia?.url.replace(
        '{0}',
        '@2x',
    );
    const canCopy = Boolean(targetEnv);

    return (
        <section>
            <header className={styles.header}>
                <h2 className={styles.headerTitle}>
                    {isMulti
                        ? `Preview & Copy ${sourceTagsToCopy.length} Tags to Environment`
                        : 'Preview & Copy Tag to Environment'}
                </h2>
                <div>
                    Copy to:
                    <Selector
                        value={targetEnvName}
                        onChange={(e) => setTargetEnvName(e.target.value)}
                    >
                        <option disabled value="">
                            Select environment
                        </option>
                        {otherEnvs.map((env) => (
                            <option key={env.name} value={env.name}>
                                {env.name}
                            </option>
                        ))}
                    </Selector>
                </div>
            </header>
            {isMulti && (
                <div className={styles.tabs} role="tablist">
                    {sourceTagsToCopy.map((t, i) => {
                        const entry = targetEnvName
                            ? buildTagCopyEntry(t, targetTags)
                            : undefined;
                        return (
                            <button
                                key={t.id}
                                role="tab"
                                aria-selected={i === safeTabIndex}
                                className={cn(
                                    styles.tab,
                                    i === safeTabIndex && styles.tabActive,
                                )}
                                onClick={() => setActiveTabIndex(i)}
                            >
                                <span>{t.name.en || t.stringId}</span>
                                {entry && (
                                    <span className={styles.tabBadge}>
                                        {entry.isNew ? 'new' : 'update'}
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </div>
            )}
            <table className={styles.previewTable}>
                <thead>
                    <tr>
                        <th />
                        <th>
                            From{' '}
                            <b className={styles.envName}>{currentEnv?.name}</b>
                        </th>
                        <th>
                            {targetEnvName ? (
                                <>
                                    To{' '}
                                    <b className={styles.envName}>
                                        {targetEnvName}
                                    </b>{' '}
                                    {activeCopyEntry?.isNew
                                        ? '(will be created)'
                                        : '(will be updated)'}
                                </>
                            ) : (
                                'Select target environment'
                            )}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>String ID</td>
                        <td>{activeTag.stringId}</td>
                        <td>
                            {activeCopyEntry?.targetTag?.stringId ??
                                activeTag.stringId}
                        </td>
                    </tr>
                    <tr>
                        <td>Name (en)</td>
                        <td>{activeTag.name.en ?? ''}</td>
                        <td>
                            {activeCopyEntry?.targetTag?.name.en ?? '—'}
                            {activeCopyEntry?.hasNameMismatch && (
                                <div className={styles.warning}>
                                    ⚠ name mismatch — target will be overwritten
                                    with source name.
                                </div>
                            )}
                        </td>
                    </tr>
                    <tr>
                        <td>Synonyms</td>
                        <td>{activeTag.synonyms.join(', ') || '—'}</td>
                        <td>
                            {activeCopyEntry?.targetTag?.synonyms.join(', ') ||
                                '—'}
                        </td>
                    </tr>
                    <tr>
                        <td>Icon</td>
                        <td>
                            {iconUrl ? (
                                <div className={styles.icon}>
                                    <img src={iconUrl} alt="source icon" />
                                </div>
                            ) : (
                                '—'
                            )}
                        </td>
                        <td>
                            {targetIconUrl ? (
                                <div className={styles.icon}>
                                    <img
                                        src={targetIconUrl}
                                        alt="target icon"
                                    />
                                </div>
                            ) : (
                                '—'
                            )}
                        </td>
                    </tr>
                    <tr>
                        <td>Parents</td>
                        <td>
                            <TagRelationPreview
                                relationTagIds={activeTag.parents}
                                sourceTags={sourceTags}
                                targetTags={targetTags}
                                showTargetPlan={false}
                            />
                        </td>
                        <td>
                            {targetEnvName ? (
                                <TagRelationPreview
                                    relationTagIds={activeTag.parents}
                                    sourceTags={sourceTags}
                                    targetTags={targetTags}
                                    showTargetPlan
                                />
                            ) : (
                                '—'
                            )}
                        </td>
                    </tr>
                    <tr>
                        <td>Children</td>
                        <td>
                            <TagRelationPreview
                                relationTagIds={activeTag.children}
                                sourceTags={sourceTags}
                                targetTags={targetTags}
                                showTargetPlan={false}
                            />
                        </td>
                        <td>
                            {targetEnvName ? (
                                <TagRelationPreview
                                    relationTagIds={activeTag.children}
                                    sourceTags={sourceTags}
                                    targetTags={targetTags}
                                    showTargetPlan
                                />
                            ) : (
                                '—'
                            )}
                        </td>
                    </tr>
                    {activeCopyEntry?.targetTag &&
                        hasTagRelationsChanged(
                            activeTag,
                            activeCopyEntry.targetTag,
                            sourceTags,
                            targetTags,
                        ) && (
                            <tr>
                                <td />
                                <td colSpan={2}>
                                    <div className={styles.warning}>
                                        ⚠ Relation changes — the target's
                                        current parents/children will be
                                        replaced with the source's.
                                    </div>
                                </td>
                            </tr>
                        )}
                </tbody>
            </table>
            <Footer>
                <p className={styles.paragraph}>
                    Please note, this process can't be reversed. Please confirm
                    the tag data before copying.
                </p>
                <div>
                    <Button kind={ButtonKind.SECONDARY} onClick={onClose}>
                        Cancel
                    </Button>
                    <Button
                        kind={ButtonKind.PRIMARY}
                        disabled={!canCopy}
                        onClick={() =>
                            canCopy && targetEnv && copyAll(targetEnv)
                        }
                    >
                        {isMulti ? 'Copy tags' : 'Copy tag'}
                    </Button>
                </div>
            </Footer>
        </section>
    );
}

export default CopyTag;
